/** Attraction type options */
export const ATTRACTION_TYPES = ['cultural', 'natural', 'entertainment', 'religious', 'historical'] as const;
export type AttractionType = (typeof ATTRACTION_TYPES)[number];

/** Core attraction entity */
export interface Attraction {
  id: string;
  attractionId: string;
  name: string;
  description: string;
  city: string;
  state: string | null;
  country: string;
  countryCode: string;
  lePlaceId: string | null;
  latitude: number;
  longitude: number;
  type: AttractionType;
  popularityRank: number;
  imageUrl: string | null;
  active: boolean;
  offerCount: number;
}

/** Response from image mutation endpoints (PATCH image, DELETE image, PATCH location-image). */
export interface AttractionImageMutationResponse {
  attractionId: string;
  imageUrl: string | null;
  imageCurated?: boolean;
  locationImageUrl?: string | null;
  /**
   * ISO-8601 timestamp used as CDN cache-buster on the next GET.
   * Optional: absent while the paired svc-experiences PR has not shipped.
   */
  updatedAt?: string;
}

/** City with attraction coverage data */
export interface CityWithCoverage {
  city: string;
  country: string;
  countryCode: string;
  lePlaceId: string | null;
  attractionCount: number;
  coveredCount: number;
  coverageRate: number;
}

/** Offer matched to an attraction */
export interface MatchedOffer {
  id: string;
  title: string;
  shortDescription: string;
  slug: string;
  provider: string;
  matchType: 'PROXIMITY' | 'DIRECT_TITLE' | 'KEYWORD' | 'AI_SEMANTIC';
  relevanceScore: number;
  latitude: number | null;
  longitude: number | null;
}

/** Image available for an attraction from linked offers */
export interface AttractionImage {
  imageId: string;
  url: string;
  isHero: boolean;
  offerTitle: string;
  offerId: string;
}

/** Query params for listing attractions */
export interface ListAttractionsParams {
  city?: string;
  country?: string;
  countryCode?: string;
  type?: string;
  sort?: string;
  limit?: number;
  offset?: number;
  includeInactive?: boolean;
}

/** API response for a single attraction */
export interface GetAttractionResponse {
  result: Attraction;
}

/** API response for listing attractions */
export interface ListAttractionsResponse {
  result: Attraction[];
  total: number;
}

/** API response for listing cities */
export interface ListAttractionCitiesResponse {
  result: CityWithCoverage[];
}

/** API response for listing offers */
export interface ListAttractionOffersResponse {
  result: MatchedOffer[];
  total: number;
}

/** API response for listing images */
export interface ListAttractionImagesResponse {
  result: AttractionImage[];
}

/** Query params for listing attraction offers */
export interface ListAttractionOffersParams {
  limit?: number;
  offset?: number;
}

/** City-level stats within a country group */
export interface CityGroupStats {
  city: string;
  attractionCount: number;
  coveredCount: number;
  coverageRate: number;
  totalOffers: number;
}

/** Country-level stats */
export interface CountryStats {
  countryCode: string;
  country: string;
  attractionCount: number;
  coveredCount: number;
  coverageRate: number;
  totalOffers: number;
  cityCount: number;
  cities?: CityGroupStats[];
}

/** Aggregated stats result */
export interface StatsResult {
  totalAttractions: number;
  totalCovered: number;
  coverageRate: number;
  totalCountries: number;
  totalCities: number;
  totalOffers: number;
  countries: CountryStats[];
}

/** API response for stats endpoint */
export interface GetAttractionStatsResponse {
  result: StatsResult;
}

/** Query params for stats endpoint */
export interface GetAttractionStatsParams {
  city?: string;
  countryCode?: string;
  type?: string;
  groupBy?: 'city';
}

/** Formats attraction type enum for display (e.g. CULTURAL_SITE -> Cultural Site) */
export function formatAttractionType(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
